// src/App.jsx
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import './utils/logger'; // Disable console logs in production

import './amplifyConfig'; // Configure Amplify first
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppProvider } from './contexts/AppContext';
import { ChatProvider } from './contexts/ChatContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PresenceProvider } from './contexts/PresenceContext';
import { ConfirmProvider } from './hooks/useConfirm';
import Layout from './components/Layout/Layout';
import ReportsPage from './components/Admin/ReportsPage';
import ContentReportsPage from './components/Admin/ContentReportsPage';
import ReviewModerationPage from './components/Admin/ReviewModerationPage';
import AdminControlCenter from './components/Admin/AdminControlCenter';
import ContentAdminPanel from './components/Admin/ContentAdminPanel';
import AchievementsDashboard from './components/Admin/AchievementsDashboard';
import HypeRatingManager from './components/Admin/HypeRatingManager';
import { AchievementLeaderboards } from './components/Achievements';
import AchievementIntegrationExample from './components/Achievements/AchievementIntegrationExample';
import UserAchievementsDashboard from './components/Achievements/UserAchievementsDashboard';
import AchievementTest from './components/Admin/AchievementTest';
import LeaderboardDataTest from './components/Admin/LeaderboardDataTest';
import WatchlistTest from './components/Test/WatchlistTest';
import WatchedCleanup from './components/Test/WatchedCleanup';
import WatchedToggleTest from './components/Test/WatchedToggleTest';
import LegacyTableTest from './components/Test/LegacyTableTest';
import AdminRoute from './components/Auth/AdminRoute';
import MyReports from './components/Profile/MyReports';
import DataDeletion from './components/Legal/DataDeletion';
import Settings from './components/Profile/Settings';
import Search from './components/Search/Search';
import AboutUs from './components/Pages/AboutUs';
import ContactUs from './components/Pages/ContactUs';
import SupportTicketForm from './components/Support/SupportTicketForm';
import SupportCenter from './components/Support/SupportCenter';
import UserTicketTracker from './components/Support/UserTicketTracker';
import PublicTicketTracker from './components/Support/PublicTicketTracker';
import PublicTicketsList from './components/Support/PublicTicketsList';
import SupportDashboard from './components/Admin/SupportDashboard';
import SupportTicketManager from './components/Admin/SupportTicketManager';
import Watchlist from './components/Movies/Watchlist';
import UserWatchlist from './components/Movies/UserWatchlist';
import UserWatchedList from './components/Movies/UserWatchedList';
import MovieCard from './components/Movies/MovieCard';
import Releases from './components/Movies/Releases';
import { WatchlistContentView, WatchlistDashboard } from './components/Watchlist';
import BookmarksManager from './components/Profile/BookmarksManager';
import BlockedUsersManager from './components/Profile/BlockedUsersManager';
import NewsPage from './components/News/NewsPage';
import NewsDetailPage from './components/News/NewsDetailPage';
import AdminNewsPage from './components/Admin/NewsManagement/AdminNewsPage';
import AuthWrapper from './components/Auth/AuthWrapper';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PublicRoute from './components/Auth/PublicRoute';
import ForgotPassword from './components/Auth/ForgotPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import NotificationDisplay from './components/Common/NotificationDisplay';
import { notificationService } from './services/notificationService';
import { LoadingScreen } from './components/Branding';
import AchievementProvider from './components/Layout/AchievementProvider';
import AchievementTestPanel from './components/Testing/AchievementTestPanel';
import { useAchievementNotifications } from './hooks/useAchievementNotifications';
import TitleNotificationSystem from './components/Achievements/TitleNotificationSystem';
import BadgeNotificationSystem from './components/Achievements/BadgeNotificationSystem';
import AchievementNotificationSystem from './components/Achievements/AchievementNotificationSystem';
import AuthDebugPanel from './components/Debug/AuthDebugPanel';
import AuthDiagnostic from './components/Debug/AuthDiagnostic';
import BlockUserTest from './components/Debug/BlockUserTest';
import EmergencySignIn from './components/Auth/EmergencySignIn';
import EventAPIDemo from './components/EventAPIDemo';
import SimplePresenceDemo from './components/SimplePresenceDemo';
import EventAPIIntegrationTest from './components/EventAPIIntegrationTest';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import './styles/ambientColors.css';
import './styles/mobileLayout.css';
import { Amplify } from 'aws-amplify';

// Lazy load heavy components
const Feed = lazy(() => import('./components/Feed/Feed'));
const PostPage = lazy(() => import('./components/Feed/PostPage'));
const Movies = lazy(() => import('./components/Movies/MovieList'));
const MoviesPage = lazy(() => import('./components/Movies/MoviesPage'));
const ShowsPage = lazy(() => import('./components/Shows/ShowsPage'));
const MovieDetailPage = lazy(() => import('./pages/MovieDetailPage'));
const ShowDetailPage = lazy(() => import('./pages/ShowDetailPage'));
const Chat = lazy(() => import('./components/Chat/Chat'));
const Profile = lazy(() => import('./components/Profile/UserProfile'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));

// Amplify config verification
if (process.env.NODE_ENV === 'development') {
  setTimeout(() => {
    try {
      const config = Amplify.getConfig();
      console.log('🔍 App.jsx - Complete Amplify Config:', {
        Auth: config.Auth,
        hasAuth: !!config.Auth,
        hasCognito: !!config.Auth?.Cognito,
        userPoolId: config.Auth?.Cognito?.userPoolId,
        userPoolClientId: config.Auth?.Cognito?.userPoolClientId,
        region: config.Auth?.Cognito?.region
      });
      
      if (!config.Auth?.Cognito?.userPoolId) {
        console.error('🚨 CRITICAL ERROR: UserPool ID missing from Amplify config!');
        console.error('This will cause "Auth UserPool not configured" errors');
      }
    } catch (error) {
      console.error('🚨 Failed to get Amplify config in App.jsx:', error);
    }
  }, 500);
}

// Create theme with dark mode support
const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      // main: mode === 'dark' ? '#ffc107' : '#f57c00',
      //light: mode === 'dark' ? '#ffcd38' : '#ffb300',
      //dark: mode === 'dark' ? '#ff8f00' : '#e65100',
      main: mode === 'dark' ? '#ffc107' : '#1976d2',
      light: mode === 'dark' ? '#ffcd38' : '#42a5f5',
      dark: mode === 'dark' ? '#ff8f00' : '#1565c0',
    },
    secondary: {
      main: mode === 'dark' ? '#03dac6' : '#dc004e',
      light: mode === 'dark' ? '#4fd8c7' : '#e91e63',
      dark: mode === 'dark' ? '#018786' : '#c51162',
    },
    background: {
      default: mode === 'dark' ? '#121212' : '#f5f5f5',
      paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#000000',
      secondary: mode === 'dark' ? '#b3b3b3' : '#666666',
    },
    error: {
      main: mode === 'dark' ? '#cf6679' : '#f44336',
    },
    warning: {
      main: mode === 'dark' ? '#ffcc02' : '#ff9800',
    },
    success: {
      main: mode === 'dark' ? '#4caf50' : '#2e7d32',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === 'dark' 
            ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
            : '0 2px 12px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
          color: mode === 'dark' ? '#ffffff' : '#000000',
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

// Page tracking component
function PageTracker() {
  const location = useLocation();
  
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  return null;
}

// Component to initialize achievement notifications inside providers
function AchievementNotificationInitializer() {
  useAchievementNotifications();
  return null;
}

function App() {
  const [themeMode, setThemeMode] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme-mode');
    return savedTheme || 'dark';
  });

  const theme = React.useMemo(() => createAppTheme(themeMode), [themeMode]);

  // Request notification permission on app start
  React.useEffect(() => {
    notificationService.requestPermission().catch(err => {
      console.log('Notification permission denied:', err);
    });
  }, []);

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  // Custom Authenticator components - removed to avoid conflicts
  // const components = {
  //   SignIn: (props) => <AuthWrapper {...props} />,
  //   SignUp: (props) => <AuthWrapper {...props} />,
  // };

  return (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider themeMode={themeMode} toggleTheme={toggleTheme}>
          <AuthProvider>
            <AchievementProvider>
              <ChatProvider>
                <NotificationProvider>
                  <ConfirmProvider>
                    <PresenceProvider>
                    <AchievementNotificationInitializer />
                  <Router>
                    <PageTracker />
                    <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                    {/* Public Routes - No Authentication Required */}
                    <Route path="/" element={
                      <PublicRoute allowAuthenticated={true}>
                        <Layout>
                          <MoviesPage />
                        </Layout>
                      </PublicRoute>
                    } />
                    <Route path="/movies" element={
                      <PublicRoute allowAuthenticated={true}>
                        <Layout>
                          <MoviesPage />
                        </Layout>
                      </PublicRoute>
                    } />
                    <Route path="/shows" element={
                      <PublicRoute allowAuthenticated={true}>
                        <Layout>
                          <ShowsPage />
                        </Layout>
                      </PublicRoute>
                    } />
                    <Route path="/movie/:id" element={
                      <Layout>
                        <MovieDetailPage />
                      </Layout>
                    } />
                    <Route path="/movies/:id" element={
                      <Layout>
                        <MovieDetailPage />
                      </Layout>
                    } />
                    <Route path="/show/:id" element={
                      <Layout>
                        <ShowDetailPage />
                      </Layout>
                    } />
                    <Route path="/shows/:id" element={
                      <Layout>
                        <ShowDetailPage />
                      </Layout>
                    } />
                    <Route path="/movies/list" element={
                      <Layout>
                        <MoviesPage />
                      </Layout>
                    } />
                    <Route path="/movies/trending" element={
                      <Layout>
                        <Movies category="TRENDING" />
                      </Layout>
                    } />
                    <Route path="/movies/theaters" element={
                      <Layout>
                        <Movies category="IN_THEATERS" />
                      </Layout>
                    } />
                    <Route path="/movies/upcoming" element={
                      <Layout>
                        <Movies category="UPCOMING" />
                      </Layout>
                    } />
                    <Route path="/achievements" element={
                      <ProtectedRoute>
                        <Layout>
                          <UserAchievementsDashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/leaderboards" element={
                      <PublicRoute allowAuthenticated={true}>
                        <Layout>
                          <AchievementLeaderboards />
                        </Layout>
                      </PublicRoute>
                    } />
                    <Route path="/data-deletion" element={<DataDeletion />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/about" element={<Layout><AboutUs /></Layout>} />
                    <Route path="/contact" element={<Layout><ContactUs /></Layout>} />
                    <Route path="/support" element={<Layout><SupportCenter /></Layout>} />
                    <Route path="/support/track" element={<Layout><PublicTicketTracker /></Layout>} />
                    <Route path="/support/tickets" element={<Layout><PublicTicketsList /></Layout>} />
                    <Route path="/support/my-tickets" element={<ProtectedRoute><Layout><UserTicketTracker /></Layout></ProtectedRoute>} />
                    
                    {/* Auth Routes */}
                    <Route path="/auth" element={
                      <PublicRoute redirectIfAuthenticated={true}>
                        <AuthWrapper />
                      </PublicRoute>
                    } />
                    <Route path="/forgot-password" element={
                      <PublicRoute redirectIfAuthenticated={true}>
                        <ForgotPassword />
                      </PublicRoute>
                    } />
                    
                    {/* Protected Routes - Authentication Required */}
                    <Route path="/feed" element={
                      <ProtectedRoute showSessionWarning={true}>
                        <Layout>
                          <Feed />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/watchlist" element={
                      <ProtectedRoute>
                        <Layout>
                          <Watchlist />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/watchlist/:watchlistId" element={
                      <ProtectedRoute>
                        <Layout>
                          <WatchlistContentView />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/my-watchlist" element={
                      <ProtectedRoute>
                        <Layout>
                          <WatchlistDashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/my-watched" element={
                      <ProtectedRoute>
                        <Layout>
                          <UserWatchedList />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/bookmarks" element={
                      <ProtectedRoute>
                        <Layout>
                          <BookmarksManager />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/blocked-users" element={
                      <ProtectedRoute>
                        <Layout>
                          <BlockedUsersManager />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Layout>
                          <Profile />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/profile/:username" element={
                      <ProtectedRoute>
                        <Layout>
                          <Profile />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/search" element={
                      <ProtectedRoute>
                        <Layout>
                          <Search />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/search/:query" element={
                      <ProtectedRoute>
                        <Layout>
                          <Search />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/chat" element={
                      <ProtectedRoute showSessionWarning={true}>
                        <Layout>
                          <Chat />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/chat/archived" element={
                      <ProtectedRoute showSessionWarning={true}>
                        <Layout>
                          <Chat defaultTab={1} />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/chat/:conversationId" element={
                      <ProtectedRoute showSessionWarning={true}>
                        <Layout>
                          <Chat />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <AdminDashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/control-center" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <AdminControlCenter />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/content" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <ContentAdminPanel />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/hyperating" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <HypeRatingManager />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/achievements" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <AchievementsDashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/achievement-test" element={<ProtectedRoute><Layout><AchievementTest /></Layout></ProtectedRoute>} />
                    <Route path="/admin/leaderboard-data" element={<ProtectedRoute><AdminRoute><Layout><LeaderboardDataTest /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/admin/support" element={<ProtectedRoute><AdminRoute><Layout><SupportDashboard /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/admin/support/tickets" element={<ProtectedRoute><AdminRoute><Layout><SupportTicketManager /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/test/watchlist" element={<ProtectedRoute><AdminRoute><Layout><WatchlistTest /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/test/cleanup-watched" element={<ProtectedRoute><AdminRoute><Layout><WatchedCleanup /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/test/watched-toggle" element={<ProtectedRoute><AdminRoute><Layout><WatchedToggleTest /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/test/legacy-sync" element={<ProtectedRoute><AdminRoute><Layout><LegacyTableTest /></Layout></AdminRoute></ProtectedRoute>} />
                    <Route path="/test-achievements" element={<ProtectedRoute><Layout><AchievementTestPanel /></Layout></ProtectedRoute>} />
                    <Route path="/debug/auth" element={<Layout><AuthDebugPanel /></Layout>} />
                    <Route path="/debug/signin" element={<Layout><AuthDiagnostic /></Layout>} />
                    <Route path="/debug/block" element={<Layout><BlockUserTest /></Layout>} />
                    <Route path="/emergency-signin" element={<EmergencySignIn />} />
                    <Route path="/event-api-demo" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <EventAPIDemo />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/presence-demo" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <SimplePresenceDemo />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/integration-test" element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Layout>
                          <EventAPIIntegrationTest />
                        </Layout>
                      </ProtectedRoute>
                    } />

                    <Route path="/admin/reports" element={<ProtectedRoute><Layout><ReportsPage /></Layout></ProtectedRoute>} />
                    <Route path="/admin/content-reports" element={<ProtectedRoute><Layout><ContentReportsPage /></Layout></ProtectedRoute>} />
                    <Route path="/admin/review-moderation" element={<ProtectedRoute><Layout><ReviewModerationPage /></Layout></ProtectedRoute>} />
                    <Route path="/admin/news" element={<ProtectedRoute requiredRole="ADMIN"><Layout><AdminNewsPage /></Layout></ProtectedRoute>} />
                    <Route path="/my-reports" element={<ProtectedRoute><Layout><MyReports /></Layout></ProtectedRoute>} />
                    {/* <Route path="/notifications" element={<ProtectedRoute><Layout><NotificationsPage /></Layout></ProtectedRoute>} /> */}
                    <Route path="/post/:postId" element={<ProtectedRoute><Layout><PostPage /></Layout></ProtectedRoute>} />
                    
                    {/* News Routes */}
                    <Route path="/news" element={
                      <PublicRoute allowAuthenticated={true}>
                        <Layout>
                          <NewsPage />
                        </Layout>
                      </PublicRoute>
                    } />
                    <Route path="/news/:newsId" element={
                      <PublicRoute allowAuthenticated={true}>
                        <Layout>
                          <NewsDetailPage />
                        </Layout>
                      </PublicRoute>
                    } />
                  </Routes>
                  </Suspense>
                  </Router>
                    </PresenceProvider>
                  </ConfirmProvider>
                </NotificationProvider>
              </ChatProvider>
            </AchievementProvider>
          </AuthProvider>
          <NotificationDisplay />
          <AchievementNotificationSystem />
          <TitleNotificationSystem />
          <BadgeNotificationSystem />
        </AppProvider>
      </ThemeProvider>
  );
}

export default App;